import { DetailRequirement, MpxResponseError, RPBenchmarkStatus } from '@cibo/core'
import { TraitContent } from '@cibo/landmanager/src/features/DetailEditing/TraitContent'
import { useProgramModel } from '@cibo/programs'
import { Markdown, Section, getEventMutationKey, logRequestError } from '@cibo/ui'
import { useSaveIndicator } from '@cibo/ui/src/components/SaveIndicator/SaveIndicatorProvider'
import { Alert, Box, Collapse, Fade, Stack } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { groupBy } from 'ramda'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskMarkdown } from '../TaskMarkdown'
import { TaskComponentProps } from '../types'
import { resourceDetailFeatureByTraitId } from './resourceDetailFeatureMap'

const mutationKeyPrefixes = [
  'save_detail',
  'remove_detail',
  'save_bulk_details',
  'save_detail_file',
]

export const BenchmarkStatus = ({
  engagement,
  onSuccess,
  task,
  userRole,
  ownerName,
}: TaskComponentProps<RPBenchmarkStatus>) => {
  const { setIsDirty, setIsError, setIsSaving } = useSaveIndicator()
  const programModel = useProgramModel({ programId: engagement.programId })
  const { content } = programModel.data?.taskContent(task.id) ?? {}
  const benchmark = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === task.rule.params?.benchmarkName
  )
  useEffect(() => setIsDirty(false), [])

  const groupedTraits = useMemo(
    () => groupBy(({ traitId }) => traitId, benchmark?.details ?? []),
    [benchmark?.benchmark]
  )

  const queryClient = useQueryClient()

  const mutationListener = (event: any) => {
    if (!event.action) return

    const mutationKey = getEventMutationKey(event)

    if (mutationKey && mutationKeyPrefixes.includes(mutationKey[0])) {
      switch (event.action.type) {
        case 'loading':
          setIsError(false)
          setIsSaving(true)
          break
        case 'error':
          setIsSaving(false)
          setIsError(true)
          break
        case 'success':
          setIsSaving(false)
          break
      }
    }
  }

  useEffect(() => {
    const unsubscribe = queryClient.getMutationCache().subscribe(mutationListener)
    return unsubscribe
  }, [])

  return (
    <Stack flex={1}>
      <Stack spacing={3} flex={1}>
        {content?.markdown && (
          <Box>
            <TaskMarkdown>
              {content.markdown?.[userRole]?.replace(/\{\{name\}\}/g, ownerName)}
            </TaskMarkdown>
          </Box>
        )}

        {Object.values(groupedTraits).map((details, index) => {
          return (
            <Fragment key={index}>
              {content?.includeDefinition && (
                <Section>
                  <Box padding={3}>
                    <TraitContent traitId={details?.[0]?.traitId} />
                  </Box>
                </Section>
              )}
              <TaskEditorWrapper
                detailRequirements={details}
                engagement={engagement}
                onSuccess={onSuccess}
                ownerName={ownerName}
                userRole={userRole}
                task={task}
              />
            </Fragment>
          )
        })}
      </Stack>
    </Stack>
  )
}

const TaskEditorWrapper = ({
  detailRequirements,
  engagement,
  onSuccess,
  ownerName,
  userRole,
  task,
}: TaskComponentProps<RPBenchmarkStatus> & {
  detailRequirements: DetailRequirement[]
}) => {
  const { t } = useTranslation('@cibo/programs/BenchmarkStatusTask')

  const traitId = detailRequirements?.[0].traitId

  // caching error state to prevent error message from disappearing on next save attempt
  const [error, setError] = useState<AxiosResponse<MpxResponseError>>()
  // const Feature = resourceDetailFeatureByTraitId(detailRequirements?.[0].traitId)
  const Feature = resourceDetailFeatureByTraitId(traitId)

  let resourceIds: string[] = []

  if (Feature?.resourceType === 'field') {
    resourceIds = engagement.fields
  } else if (Feature?.resourceType === 'user' && engagement.ownedBy.userId) {
    resourceIds = [`${engagement.ownedBy.userId}`]
  } else if (Feature?.resourceType === 'workflow' && engagement.id) {
    resourceIds = [engagement.id]
  }

  const { setIsDirty, setIsError, setIsSaving } = useSaveIndicator()

  const onUpdating = () => {
    setIsError(false)
    setIsDirty(true)
    setIsSaving(true)
  }

  const handleSuccess = () => {
    setError(undefined)
    setIsSaving(false)
    onSuccess?.()
  }

  const onError = (error: AxiosResponse<MpxResponseError>) => {
    console.log('TaskEditor.onError', error)
    // @todo: Use logRequestError logic (if a request error) to distinguish this error
    Sentry.captureException(error, {
      extra: { engagementId: engagement.id, resourceIds, detailRequirements },
    })
    try {
      // @ts-ignore
      logRequestError(error, {
        engagementId: engagement.id,
        resourceIds,
        detailRequirements,
      })
    } catch (error) {
      Sentry.captureException(new Error('TaskEditor.onError: logRequestError failed'), {
        extra: { error },
      })
    }
    setIsSaving(false)
    setError(error)
    setIsError(true)
  }

  if (!Feature?.Component) {
    console.warn(`Benchmark task not supported for ${traitId}`)
    return null
  }

  const component = Feature?.Component ? (
    <Stack>
      <Feature.Component.TaskEditor
        //@ts-ignore @todo figure out how to infer type here
        detailRequirements={detailRequirements}
        engagementId={engagement.id}
        resourceIds={resourceIds}
        onUpdating={onUpdating}
        onSuccess={handleSuccess}
        onError={onError}
        userRole={userRole}
        ownerName={ownerName}
        engagement={engagement}
        // Pretty subtle type problem but reasonable that a
        // task should be available to a TaskEditor.
        // @ts-ignore
        task={task}
      />
      <Collapse in={!!error}>
        <Fade in={!!error}>
          <Alert sx={{ mt: 3 }} severity="error">
            <Markdown>
              {t('errorMd', {
                context: error?.status,
                message: error?.data?.message,
              })}
            </Markdown>
          </Alert>
        </Fade>
      </Collapse>
    </Stack>
  ) : null

  return !Feature?.Component.selfIsland ? (
    <Section sx={{ flex: 1 }}>
      <Stack padding={3} spacing={3}>
        {component}
      </Stack>
    </Section>
  ) : (
    component
  )
}
